import { api } from './_client';
import type {
  ObjectShare,
  ObjectShareCreateRequest,
  ObjectShareGrantResponse,
  ObjectShareListResponse,
  ShareCounterparties,
} from '../../types/api';

/**
 * File and folder sharing (#1182 Stage 4 slice 4).
 *
 * A separate domain module from `flightShares.ts` (STD-004), mirroring the
 * backend split: these routes are `/api/object-shares`, they resolve
 * `fleet.file_objects` rows, and everything they take or return is keyed on a
 * registry **id** — never an S3 key and never a display path.
 */
export const objectSharesApi = {
  /** Shares the caller's org granted or received. All filters are optional. */
  async listObjectShares(params?: {
    objectId?: string;
    includeRevoked?: boolean;
  }): Promise<ObjectShareListResponse> {
    const query = new URLSearchParams();
    if (params?.objectId) query.append('object_id', params.objectId);
    // Only sent when true — the backend default is false, and an explicit
    // `include_revoked=false` would make the query key noisier for no gain.
    if (params?.includeRevoked) query.append('include_revoked', 'true');

    const qs = query.toString();
    const response = await api.get<ObjectShareListResponse>(
      `/api/object-shares${qs ? `?${qs}` : ''}`
    );
    return response.data;
  },

  /**
   * Grant read/write on files or folders to another organization.
   *
   * Sharing a folder is this same call with the folder's id. There is no
   * recursive flag because nothing recursive happens: one row on the folder
   * node covers the subtree, now and as it grows.
   */
  async grantObjectShares(body: ObjectShareCreateRequest): Promise<ObjectShareGrantResponse> {
    const response = await api.post<ObjectShareGrantResponse>('/api/object-shares', body);
    return response.data;
  },

  /**
   * Revoke a share by stamping `revoked_at`.
   *
   * A POST, not a DELETE: the row is never deleted (it is the audit trail of
   * who could see what, when), and only the granting org may do it. Revoking a
   * folder share withdraws the whole subtree in one row.
   */
  async revokeObjectShare(shareId: string): Promise<ObjectShare> {
    const response = await api.post<ObjectShare>(`/api/object-shares/${shareId}/revoke`);
    return response.data;
  },

  /**
   * Organizations this caller may legally share files with, plus its own org id.
   *
   * Served by the object-shares router rather than borrowed from
   * `/api/flight-shares/organizations`: the two answer the same question today
   * only because the hub-and-spoke rule is currently identical for both, and
   * pointing this UI at the flight route would silently couple them.
   */
  async getObjectShareCounterparties(): Promise<ShareCounterparties> {
    const response = await api.get<ShareCounterparties>('/api/object-shares/organizations');
    return response.data;
  },
};
